/**
 * React Query mutation hook for deleting a component rule.
 *
 * Confirmation UX lives in the component config page; the hook itself
 * only wraps the raw DELETE call and wipes the relevant cache entries
 * so the row disappears immediately after success.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { componentRulesService } from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/** Variables accepted by `useDeleteComponentRule().mutate(...)`. */
export interface DeleteComponentRuleVariables {
  id: string;
  componentId?: string;
}

/** Mutation hook for `DELETE /claimflow/component-rules/{id}`. */
export function useDeleteComponentRule(): UseMutationResult<
  void,
  Error,
  DeleteComponentRuleVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: DeleteComponentRuleVariables) =>
      componentRulesService.deleteRule(id),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.lists(),
      });
      if (variables.componentId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: componentRulesKeys.list(variables.componentId),
        });
      }
      // Remove the stale detail cache entry outright — the rule no
      // longer exists, so a subsequent query would 404 anyway.
      queryClient.removeQueries({
        queryKey: componentRulesKeys.detail(variables.id),
      });
    },
  });
}
